import { Config } from 'cms-types'

import { NextSearchParams } from '@/common/types'
import { logger } from '@/helpers/logger'

export const CMS_URL = process.env['PAYLOAD_INTERNAL_URL'] ?? process.env.NEXT_PUBLIC_PAYLOAD_URL

export type Options = Partial<{
  preview: NextSearchParams['preview']
  locale: string
  depth: string
  limit: string
  type: string
}>

const getOptionParam = (options: Options, key: keyof Options, customName?: string): string => {
  const value = options[key]
  if (!value) return ''

  const name = customName || key
  return `${name}=${value}`
}

export const getCmsOptionsAsSearchParams = (options: Options = {}): string => {
  const params = [
    getOptionParam(options, 'preview', 'draft') || 'draft=false',
    getOptionParam(options, 'locale'),
    getOptionParam(options, 'depth'),
    getOptionParam(options, 'limit'),
    getOptionParam(options, 'type', 'where[type][equals]'),
  ]

  return params.filter(Boolean).join('&')
}

export const errorWithCustomMessage = (error: unknown, type: string, url: string): Error => {
  return new Error(
    `Failed to fetch ${type} from CMS [${url}]: ${
      error instanceof Error ? error.message : String(error)
    }`,
    error instanceof Error ? { cause: error } : undefined,
  )
}

export const getCollection = async <T extends keyof Config['collections']>(
  collection: T,
  {
    searchParams = {},
    log = true,
  }: {
    searchParams?: Options
    log?: boolean
  } = {},
): Promise<Config['collections'][T][]> => {
  const url = `${CMS_URL}/api/${collection}?${getCmsOptionsAsSearchParams(searchParams)}`

  try {
    const response = await fetch(url)

    if (process.env['NODE_ENV'] === 'production' && log) {
      logger.debug('CMS REQ COLLECTION', url)
    }

    const { docs } = await response.json()

    return docs
  } catch (error) {
    throw errorWithCustomMessage(error, 'collection', url)
  }
}
